import React, { useMemo } from 'react';
import { Form, FormInstance, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import ActionTypes from '@utils/deviceActions';
import rolesConstants from '@utils/rolesConstants';
import { IDevice } from '@pages/Assets/Inventory/types';
import useNetworkBlock from './useNetworkBlock';
import useNetworkUnlock from './useNetworkUnlock';

interface NetworkBlockUnlockProps {
  devices: IDevice[];
  form: FormInstance<any>;
  isUpload?: boolean;
}

const useNetworkBlockUnlock = ({
  devices,
  form,
  isUpload,
}: NetworkBlockUnlockProps): DeviceActionProps => {
  const { t } = useTranslation(['actions-modal', 'device-actions']);
  const blockUnlockType = Form.useWatch('blockUnlockType', form);

  const networkBlockAction = useNetworkBlock({ devices, isUpload, form });
  const networkUnlockAction = useNetworkUnlock({ devices, isUpload, form });

  const selectedAction: DeviceActionProps = useMemo(() => {
    const actions: { [key: number]: DeviceActionProps } = {
      [ActionTypes.NETWORK_BLOCK]: networkBlockAction,
      [ActionTypes.NETWORK_UNLOCK]: networkUnlockAction,
    };

    return actions[blockUnlockType];
  }, [networkBlockAction, networkUnlockAction, blockUnlockType]);

  const blockUnlockTypeOptions = useMemo(
    () => [
      {
        label: t('block-unlock.block'),
        value: ActionTypes.NETWORK_BLOCK,
      },
      {
        label: t('block-unlock.unlock'),
        value: ActionTypes.NETWORK_UNLOCK,
      },
    ],
    [t],
  );

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: t('block-unlock.action'),
        name: 'blockUnlockType',
        showWhenUpload: true,
        content: (
          <Radio.Group optionType="button" options={blockUnlockTypeOptions} />
        ),
      },
    ];

    if (selectedAction?.steps) {
      defaultSteps.push(...selectedAction?.steps);
    }

    return defaultSteps;
  }, [blockUnlockTypeOptions, selectedAction?.steps, t]);

  return {
    ...selectedAction,
    title: t(
      `device-actions:labels.common.${rolesConstants.NETWORK_BLOCK_UNLOCK}`,
    ),
    uploadPosition: 1,
    steps,
    payloadToSend: selectedAction?.payloadToSend,
  };
};

export default useNetworkBlockUnlock;
